/**
 * GameWindow - A draggable, resizable window component.
 * Simplified from windowlite's Window class — drag, resize, close only.
 *
 * Features:
 * - Container-scoped positioning
 * - 8-directional resizing with edge/corner handles
 * - Drag via header with pointer capture
 * - Mobile/touch compatible via Pointer Events
 * - Z-index/focus management
 * - Show/hide/toggle with animation
 */

const DEFAULT_OPTIONS = {
  width: 400,
  height: 300,
  minWidth: 200,
  minHeight: 150,
  maxWidth: null,
  maxHeight: null,
  x: 50,
  y: 50,
  resizable: true,
  draggable: true,
  closable: true,
  animated: true,
  safeMargin: 8,
  constrainToContainer: true,
  className: '',
  icon: null,
  onClose: null,
  onShow: null,
  onHide: null,
  onResize: null,
  onMove: null,
  onFocus: null,
};

export default class GameWindow {
  constructor(options) {
    if (!options.container) throw new Error('GameWindow: container is required');
    if (!options.id) throw new Error('GameWindow: id is required');
    if (!options.title) throw new Error('GameWindow: title is required');

    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.container = options.container;
    this.id = options.id;
    this.title = options.title;

    // Ensure container has positioning context
    const pos = getComputedStyle(this.container).position;
    if (pos === 'static') this.container.style.position = 'relative';

    // State
    this.element = null;
    this.header = null;
    this.content = null;
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = null;
    this._activePointerId = null;
    this._activeHandle = null;
    this._focused = false;

    // Drag state
    this.dragStartX = 0;
    this.dragStartY = 0;

    // Resize state
    this.startX = 0;
    this.startY = 0;
    this.startWidth = 0;
    this.startHeight = 0;
    this.startLeft = 0;
    this.startTop = 0;

    this._createWindow();
    this._attachEventListeners();

    // Ensure fit after creation
    setTimeout(() => this.ensureFitInContainer(), 0);
  }

  // ---- DOM Creation ----

  _createWindow() {
    this.element = document.createElement('div');
    this.element.className = 'gw-window';
    if (this.options.className) {
      this.element.classList.add(...this.options.className.split(' ').filter(Boolean));
    }
    this.element.id = `gw-${this.id}`;
    this.element.style.width = `${this.options.width}px`;
    this.element.style.height = `${this.options.height}px`;
    this.element.style.left = `${this.options.x}px`;
    this.element.style.top = `${this.options.y}px`;
    this.element.style.zIndex = '3000';
    this.element.setAttribute('role', 'dialog');

    // Header
    this.header = document.createElement('div');
    this.header.className = 'gw-header';
    this.header.style.touchAction = 'none';

    const titleContainer = document.createElement('div');
    titleContainer.className = 'gw-title-container';

    if (this.options.icon) {
      const iconEl = document.createElement('div');
      iconEl.className = 'gw-icon';
      if (typeof this.options.icon === 'string') {
        if (this.options.icon.trim().startsWith('<')) {
          iconEl.innerHTML = this.options.icon;
        } else {
          const img = document.createElement('img');
          img.src = this.options.icon;
          img.alt = '';
          iconEl.appendChild(img);
        }
      }
      titleContainer.appendChild(iconEl);
    }

    const titleEl = document.createElement('div');
    titleEl.className = 'gw-title';
    titleEl.textContent = this.title;
    titleContainer.appendChild(titleEl);

    const controls = document.createElement('div');
    controls.className = 'gw-controls';

    if (this.options.closable) {
      const closeBtn = document.createElement('button');
      closeBtn.className = 'gw-close-btn';
      closeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>';
      closeBtn.setAttribute('aria-label', 'Close window');
      closeBtn.setAttribute('type', 'button');
      closeBtn.onclick = (e) => {
        e.stopPropagation();
        if (this.options.onClose) this.options.onClose();
        this.hide();
      };
      controls.appendChild(closeBtn);
    }

    this.header.appendChild(titleContainer);
    this.header.appendChild(controls);

    // Content
    this.content = document.createElement('div');
    this.content.className = 'gw-content';

    // Resize handles
    if (this.options.resizable) {
      const directions = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
      directions.forEach(dir => {
        const handle = document.createElement('div');
        handle.className = `gw-resize gw-resize-${dir}`;
        handle.dataset.direction = dir;
        handle.style.touchAction = 'none';
        this.element.appendChild(handle);
      });
    }

    this.element.appendChild(this.header);
    this.element.appendChild(this.content);
    this.container.appendChild(this.element);
  }

  // ---- Event Listeners ----

  _attachEventListeners() {
    GameWindow._installGlobalPointerHandlers();

    if (this.options.draggable) {
      this.header.addEventListener('pointerdown', (e) => this._startDrag(e), { passive: false });
    }

    if (this.options.resizable) {
      this.element.addEventListener('pointerdown', (e) => {
        const handle = e.target.closest('.gw-resize');
        if (handle) this._startResize(e, handle);
      }, { passive: false });
    }

    this.element.addEventListener('pointerdown', () => this.focus(), { passive: true });

    // Prevent wheel events from reaching game zoom handler
    this.element.addEventListener('wheel', (e) => {
      e.stopPropagation();
    }, { passive: false });

    // Stop touch events from propagating to the game camera controller.
    // Without this, dragging/scrolling inside windows rotates the camera on mobile.
    this.element.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });
    this.element.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: true });
    this.element.addEventListener('touchend', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }

  static _installGlobalPointerHandlers() {
    if (GameWindow._globalsInstalled) return;
    GameWindow._globalsInstalled = true;
    GameWindow._active = null;

    const onMove = (e) => {
      const a = GameWindow._active;
      if (!a || !a.win) return;
      if (a.pointerId != null && e.pointerId != null && e.pointerId !== a.pointerId) return;
      if (a.mode === 'drag') a.win._drag(e);
      else if (a.mode === 'resize') a.win._resize(e);
    };

    const onUp = (e) => {
      const a = GameWindow._active;
      if (!a || !a.win) return;
      if (a.pointerId != null && e.pointerId != null && e.pointerId !== a.pointerId) return;
      try {
        if (a.mode === 'drag') a.win._stopDrag();
        else if (a.mode === 'resize') a.win._stopResize();
      } finally {
        GameWindow._active = null;
      }
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onUp, { passive: true });
    document.addEventListener('pointercancel', onUp, { passive: true });
  }

  // ---- Container Bounds ----

  _getContainerBounds() {
    const rect = this.container.getBoundingClientRect();
    return {
      width: this.container.clientWidth || rect.width,
      height: this.container.clientHeight || rect.height,
      left: 0,
      top: 0,
    };
  }

  // ---- Dragging ----

  _startDrag(e) {
    if (e.button !== 0) return;
    if (e.target.closest('.gw-close-btn')) return;

    e.preventDefault();
    this.isDragging = true;
    this.focus();

    this._activePointerId = e.pointerId ?? null;
    GameWindow._active = { win: this, mode: 'drag', pointerId: this._activePointerId };

    const containerRect = this.container.getBoundingClientRect();
    this.dragStartX = e.clientX - containerRect.left - this.element.offsetLeft;
    this.dragStartY = e.clientY - containerRect.top - this.element.offsetTop;

    this.element.classList.add('gw-dragging');
    this.element.style.cursor = 'move';
    document.body.style.cursor = 'move';

    if (this._activePointerId != null && this.element.setPointerCapture) {
      try { this.element.setPointerCapture(this._activePointerId); } catch {}
    }
  }

  _drag(e) {
    if (!this.isDragging) return;

    const containerRect = this.container.getBoundingClientRect();
    const bounds = this._getContainerBounds();
    const margin = this.options.safeMargin;

    let newX = e.clientX - containerRect.left - this.dragStartX;
    let newY = e.clientY - containerRect.top - this.dragStartY;

    if (this.options.constrainToContainer) {
      const w = this.element.offsetWidth;
      const h = this.element.offsetHeight;
      newX = Math.max(bounds.left + margin, Math.min(newX, bounds.left + bounds.width - w - margin));
      newY = Math.max(bounds.top + margin, Math.min(newY, bounds.top + bounds.height - h - margin));
    }

    this.element.style.left = `${newX}px`;
    this.element.style.top = `${newY}px`;

    if (this.options.onMove) this.options.onMove(newX, newY);
  }

  _stopDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.element.classList.remove('gw-dragging');
    this.element.style.cursor = '';
    document.body.style.cursor = '';

    if (this._activePointerId != null && this.element.releasePointerCapture) {
      try { this.element.releasePointerCapture(this._activePointerId); } catch {}
    }
    this._activePointerId = null;
  }

  // ---- Resizing ----

  _startResize(e, handle) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    this.isResizing = true;
    this.resizeDirection = handle.dataset.direction;
    this.focus();

    this._activePointerId = e.pointerId ?? null;
    GameWindow._active = { win: this, mode: 'resize', pointerId: this._activePointerId };

    const containerRect = this.container.getBoundingClientRect();
    this.startX = e.clientX - containerRect.left;
    this.startY = e.clientY - containerRect.top;
    this.startWidth = this.element.offsetWidth;
    this.startHeight = this.element.offsetHeight;
    this.startLeft = this.element.offsetLeft;
    this.startTop = this.element.offsetTop;

    this.element.classList.add('gw-resizing');
    this._activeHandle = handle;
    handle.classList.add('gw-resizing');
    document.body.style.cursor = getComputedStyle(handle).cursor;

    if (this._activePointerId != null && this.element.setPointerCapture) {
      try { this.element.setPointerCapture(this._activePointerId); } catch {}
    }
  }

  _resize(e) {
    if (!this.isResizing) return;

    const containerRect = this.container.getBoundingClientRect();
    const clientX = e.clientX - containerRect.left;
    const clientY = e.clientY - containerRect.top;
    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;
    const dir = this.resizeDirection;

    const bounds = this._getContainerBounds();
    const margin = this.options.safeMargin;

    let left = this.startLeft;
    let top = this.startTop;
    let right = this.startLeft + this.startWidth;
    let bottom = this.startTop + this.startHeight;

    if (dir.includes('e')) right = this.startLeft + this.startWidth + deltaX;
    if (dir.includes('w')) left = this.startLeft + deltaX;
    if (dir.includes('s')) bottom = this.startTop + this.startHeight + deltaY;
    if (dir.includes('n')) top = this.startTop + deltaY;

    // Clamp to container
    if (this.options.constrainToContainer) {
      left = Math.max(bounds.left + margin, left);
      top = Math.max(bounds.top + margin, top);
      right = Math.min(bounds.left + bounds.width - margin, right);
      bottom = Math.min(bounds.top + bounds.height - margin, bottom);
    }

    // Min/max size
    const minW = this.options.minWidth;
    const minH = this.options.minHeight;
    const maxW = this.options.maxWidth || Infinity;
    const maxH = this.options.maxHeight || Infinity;

    if ((right - left) < minW) {
      if (dir.includes('w') && !dir.includes('e')) left = right - minW;
      else right = left + minW;
    }
    if ((right - left) > maxW) {
      if (dir.includes('w') && !dir.includes('e')) left = right - maxW;
      else right = left + maxW;
    }
    if ((bottom - top) < minH) {
      if (dir.includes('n') && !dir.includes('s')) top = bottom - minH;
      else bottom = top + minH;
    }
    if ((bottom - top) > maxH) {
      if (dir.includes('n') && !dir.includes('s')) top = bottom - maxH;
      else bottom = top + maxH;
    }

    // Final container clamp
    if (this.options.constrainToContainer) {
      left = Math.max(bounds.left + margin, left);
      top = Math.max(bounds.top + margin, top);
      right = Math.min(bounds.left + bounds.width - margin, right);
      bottom = Math.min(bounds.top + bounds.height - margin, bottom);
      if (right <= left) right = left + 1;
      if (bottom <= top) bottom = top + 1;
    }

    this.element.style.left = `${left}px`;
    this.element.style.top = `${top}px`;
    this.element.style.width = `${right - left}px`;
    this.element.style.height = `${bottom - top}px`;

    if (this.options.onResize) this.options.onResize(right - left, bottom - top);
  }

  _stopResize() {
    if (!this.isResizing) return;
    this.isResizing = false;
    this.resizeDirection = null;
    this.element.classList.remove('gw-resizing');
    if (this._activeHandle) {
      this._activeHandle.classList.remove('gw-resizing');
      this._activeHandle = null;
    }
    document.body.style.cursor = '';

    if (this._activePointerId != null && this.element.releasePointerCapture) {
      try { this.element.releasePointerCapture(this._activePointerId); } catch {}
    }
    this._activePointerId = null;
  }

  // ---- Fit ----

  ensureFitInContainer() {
    const bounds = this._getContainerBounds();
    const margin = this.options.safeMargin;
    const availW = Math.max(0, bounds.width - margin * 2);
    const availH = Math.max(0, bounds.height - margin * 2);

    let w = this.element.offsetWidth || this.options.width;
    let h = this.element.offsetHeight || this.options.height;

    w = Math.min(Math.max(w, Math.min(this.options.minWidth, availW)), availW);
    h = Math.min(Math.max(h, Math.min(this.options.minHeight, availH)), availH);

    this.element.style.width = `${w}px`;
    this.element.style.height = `${h}px`;

    let x = this.element.offsetLeft || this.options.x;
    let y = this.element.offsetTop || this.options.y;

    const minX = bounds.left + margin;
    const minY = bounds.top + margin;
    const maxX = Math.max(minX, minX + availW - w);
    const maxY = Math.max(minY, minY + availH - h);

    x = Math.max(minX, Math.min(x, maxX));
    y = Math.max(minY, Math.min(y, maxY));

    this.element.style.left = `${x}px`;
    this.element.style.top = `${y}px`;
  }

  // ---- Visibility ----

  show() {
    this.element.classList.remove('gw-hiding');
    this.element.classList.add('gw-active');
    this.ensureFitInContainer();
    this.focus();
    if (this.options.onShow) this.options.onShow();
    window.game?.audioSystem?.audio?.playUiOpen?.();
  }

  hide() {
    if (!this.isVisible()) return;

    const finishHide = () => {
      this.element.classList.remove('gw-active');
      this.element.classList.remove('gw-hiding');
      this.element.classList.remove('gw-focused');
      this._focused = false;
      if (this.options.onHide) this.options.onHide();
    };

    if (this.options.animated) {
      this.element.classList.add('gw-hiding');
      setTimeout(finishHide, 150);
    } else {
      finishHide();
    }
    window.game?.audioSystem?.audio?.playUiClose?.();
  }

  toggle() {
    if (this.isVisible()) this.hide();
    else this.show();
  }

  isVisible() {
    return this.element.classList.contains('gw-active');
  }

  // ---- Focus / Z-Index layering ----
  // All UI (windows, toolbar, sidebar) lives inside #container, sharing one
  // stacking context. Focus is managed purely via z-index — no DOM reparenting.

  static FOCUSED_Z = 10150;
  static BASE_WINDOW_Z = 3000;
  static BASE_SIDEBAR_Z = 3500;

  focus() {
    // Blur all sibling windows
    this.container.querySelectorAll('.gw-window').forEach(w => {
      w.classList.remove('gw-focused');
      w.style.zIndex = String(GameWindow.BASE_WINDOW_Z);
    });

    // Blur sidebar
    this.container.querySelectorAll('.gw-sidebar').forEach(s => {
      s.classList.remove('gw-focused');
      s.style.zIndex = String(GameWindow.BASE_SIDEBAR_Z);
    });

    // Focus this window
    this.element.classList.add('gw-focused');
    this._focused = true;
    this.element.style.zIndex = String(GameWindow.FOCUSED_Z);

    if (this.options.onFocus) this.options.onFocus();
  }

  /** Remove focus from all windows/sidebar, resetting z-indices to defaults. */
  static blurAll(container) {
    container.querySelectorAll('.gw-window').forEach(w => {
      w.classList.remove('gw-focused');
      w.style.zIndex = String(GameWindow.BASE_WINDOW_Z);
    });
    container.querySelectorAll('.gw-sidebar').forEach(s => {
      s.classList.remove('gw-focused');
      s.style.zIndex = String(GameWindow.BASE_SIDEBAR_Z);
    });
  }

  isFocused() {
    return this._focused;
  }

  // ---- Content ----

  setContent(content) {
    if (typeof content === 'string') {
      this.content.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      this.content.innerHTML = '';
      this.content.appendChild(content);
    }
  }

  getContentContainer() {
    return this.content;
  }

  // ---- Destroy ----

  destroy() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.header = null;
    this.content = null;
  }
}
